import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { User } from '../types';
import { playersAPI } from '../services/api';

const SITE_URL = process.env.REACT_APP_SITE_URL || '';

function initials(u: User) {
  return `${u.first_name?.[0] ?? ''}${u.last_name?.[0] ?? ''}`.toUpperCase();
}

function Avatar({ user, size = 36 }: { user: User; size?: number }) {
  const src = user.avatar_url ? `${SITE_URL}${user.avatar_url}` : null;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: src ? 'transparent' : 'var(--tt-primary)',
      color: '#fff', fontWeight: 700, fontSize: size * 0.36,
    }}>
      {src
        ? <img src={src} alt={user.first_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : initials(user)
      }
    </div>
  );
}

interface DropdownPos { top: number; left: number; width: number; }

interface Props {
  selected: User[];
  onAdd: (user: User) => void;
  onRemove: (userId: number) => void;
  excludeIds?: number[];
}

const PlayerSearchPicker: React.FC<Props> = ({ selected, onAdd, onRemove, excludeIds = [] }) => {
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState<User[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen]           = useState(false);
  const [dropPos, setDropPos]     = useState<DropdownPos | null>(null);
  const containerRef              = useRef<HTMLDivElement>(null);
  const inputWrapRef              = useRef<HTMLDivElement>(null);
  const inputRef                  = useRef<HTMLInputElement>(null);

  const blockedIds = new Set([...excludeIds, ...selected.map(u => u.id)]);

  // Reposition dropdown relative to the input on screen
  const reposition = () => {
    if (!inputWrapRef.current) return;
    const r = inputWrapRef.current.getBoundingClientRect();
    setDropPos({ top: r.bottom + 6, left: r.left, width: r.width });
  };

  // Debounced search
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await playersAPI.search({ search: query.trim() });
        if (res.success) {
          setResults((res.data?.players ?? []).filter(p => !blockedIds.has(p.id)));
          reposition();
          setOpen(true);
        }
      } catch { /* silent */ }
      finally { setSearching(false); }
    }, 280);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Keep dropdown position in sync with scroll/resize
  useEffect(() => {
    if (!open) return;
    const update = () => reposition();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !(document.getElementById('player-picker-dropdown')?.contains(target))
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = (user: User) => {
    onAdd(user);
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  const dropdown = open && dropPos ? ReactDOM.createPortal(
    <div
      id="player-picker-dropdown"
      data-portal-overlay=""
      style={{
        position: 'fixed',
        top: dropPos.top,
        left: dropPos.left,
        width: dropPos.width,
        zIndex: 9999,
        background: 'var(--tt-surface)',
        border: '1px solid var(--tt-border)',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,.16)',
        maxHeight: 220,
        overflowY: 'auto',
      }}
    >
      {results.length === 0 ? (
        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--tt-text-muted)', fontSize: '.85rem' }}>
          Niciun jucător găsit
        </div>
      ) : results.map((u, idx) => (
        <button
          key={u.id}
          type="button"
          onMouseDown={e => { e.preventDefault(); pick(u); }}
          style={{
            display: 'flex', alignItems: 'center', gap: '.75rem',
            width: '100%', padding: '.625rem 1rem', border: 'none',
            background: 'none', cursor: 'pointer', textAlign: 'left',
            borderBottom: idx < results.length - 1 ? '1px solid var(--tt-border)' : 'none',
            transition: 'background .1s', borderRadius: '4px'
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--tt-surface-2)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
        >
          <Avatar user={u} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '.875rem', color: 'var(--tt-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {u.first_name} {u.last_name}
            </div>
            {u.level_name && (
              <div style={{ fontSize: '.72rem', color: 'var(--tt-text-muted)', marginTop: 1 }}>
                {u.level_name}
              </div>
            )}
          </div>
          <span style={{
            background: '#eff6ff', color: 'var(--tt-primary)',
            border: '1px solid #bfdbfe', borderRadius: 999,
            padding: '.15rem .6rem', fontSize: '.72rem', fontWeight: 700, flexShrink: 0,
          }}>
            + Adaugă
          </span>
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <div ref={containerRef}>
      {/* Selected chips */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem', marginBottom: '.625rem' }}>
          {selected.map(u => (
            <div key={u.id} style={{
              display: 'flex', alignItems: 'center', gap: '.4rem',
              background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)',
              borderRadius: 4, padding: '.25rem .625rem .25rem .35rem',
            }}>
              <Avatar user={u} size={22} />
              <span style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--tt-text)' }}>
                {u.first_name} {u.last_name}
              </span>
              <button
                type="button"
                onClick={() => onRemove(u.id)}
                style={{
                  background: 'none', border: 'none', padding: '0 0 0 2px',
                  cursor: 'pointer', color: 'var(--tt-text-muted)',
                  display: 'flex', alignItems: 'center', lineHeight: 1,
                  fontSize: '.7rem',
                }}
              >✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Search input */}
      <div ref={inputWrapRef} style={{ position: 'relative' }}>
        
        <input
          ref={inputRef}
          type="text"
          name="player-search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) { reposition(); setOpen(true); } }}
          placeholder="Caută jucători după nume…"
          className="form-control"
          style={{ paddingLeft: 36, paddingRight: searching ? 36 : undefined, borderRadius: 10, fontSize: '.875rem' }}
        />
        {searching && (
          <span className="spinner-border spinner-border-sm"
            style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--tt-primary)' }}
          />
        )}
      </div>

      {dropdown}
    </div>
  );
};

export default PlayerSearchPicker;
